"use client";

import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { containerVariants, itemVariants } from "@/lib/animation-variants";
import {
  KOMMERS_COUNT,
  CHARGIERTE_COUNT,
  BALL_COUNT,
  useAnimatedCounter,
} from "./counters/animated-counter";

function useAnimatedCount(value: number) {
  const [mounted, setMounted] = useState(false);
  useEffect(() => setMounted(true), []);
  return useAnimatedCounter(value, 2000, mounted);
}

export default function Hero() {
  const animatedKommers = useAnimatedCount(KOMMERS_COUNT);
  const animatedChargierte = useAnimatedCount(CHARGIERTE_COUNT);
  const animatedBall = useAnimatedCount(BALL_COUNT);

  const stats = [
    { value: animatedKommers, label: "Kommersbesucher" },
    { value: animatedChargierte, label: "Chargierte" },
    { value: animatedBall, label: "Ballteilnehmer" },
  ];

  return (
    <section className="relative -mt-16 flex h-screen items-center justify-center overflow-hidden bg-gothia-cream">
      {/* Golden Wappen watermark background */}
      <div
        className="absolute inset-0 opacity-20"
        style={{
          backgroundColor: "#BDA44E",
          WebkitMask: "url(/Wappen.svg) no-repeat center/contain",
          mask: "url(/Wappen.svg) no-repeat center/contain",
        }}
      />

      {/* Content */}
      <motion.div
        variants={containerVariants}
        initial="hidden"
        animate="visible"
        className="relative z-10 flex min-h-[calc(100vh-8rem)] flex-col items-center justify-center px-4 pt-16 text-center sm:px-6 lg:px-8"
        style={{
          paddingTop: "env(safe-area-inset-top)",
          paddingBottom: "env(safe-area-inset-bottom)",
        }}
      >
        <motion.h1
          variants={itemVariants}
          className="mb-8 text-headline font-semibold leading-tight text-gothia-green sm:text-7xl md:text-8xl xl:text-9xl 2xl:text-[10rem]"
        >
          <span className="mb-2 block text-gothia-gold-dark drop-shadow-[0_1px_3px_rgba(0,0,0,0.3)]">
            150.
          </span>
          <span className="block text-4xl sm:text-5xl md:text-6xl xl:text-7xl 2xl:text-8xl">
            Stiftungsfest
          </span>
          <span className="mt-4 block text-2xl sm:text-3xl md:text-4xl xl:text-5xl 2xl:text-6xl">
            AV Gothia
          </span>
        </motion.h1>

        <motion.p
          variants={itemVariants}
          className="mx-auto mb-12 max-w-2xl text-intro text-gray-700 sm:text-xl"
        >
          11.-14. Juni 2026 • Hannover
        </motion.p>

        <motion.div
          variants={itemVariants}
          className="mb-12 flex flex-wrap items-start justify-center gap-8 sm:gap-12"
        >
          {stats.map((stat) => (
            <div key={stat.label} className="flex flex-col items-center">
              <span className="text-4xl font-semibold tabular-nums text-gothia-gold-dark drop-shadow-[0_1px_3px_rgba(0,0,0,0.2)] sm:text-5xl">
                {stat.value}
              </span>
              <span className="mt-1 text-sm text-gray-700 sm:text-base">
                {stat.label}
              </span>
            </div>
          ))}
        </motion.div>
      </motion.div>

      {/* Scroll indicator */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.8, duration: 0.8 }}
        className="absolute inset-x-0 bottom-8 z-20 mx-auto w-fit"
        style={{
          paddingBottom: "env(safe-area-inset-bottom)",
        }}
      >
        <button
          onClick={() => {
            const agendaSection = document.getElementById("agenda-section");
            if (agendaSection) {
              agendaSection.scrollIntoView({
                behavior: "smooth",
                block: "start",
              });
            }
          }}
          aria-label="Zur Agenda scrollen"
          className="group flex h-10 w-6 cursor-pointer items-center justify-center rounded-full border-2 border-gothia-green/50 transition-all duration-300 hover:scale-110 hover:border-gothia-green"
        >
          <motion.div
            animate={{ y: [0, 12, 0] }}
            transition={{ duration: 1.5, repeat: Infinity }}
            className="h-3 w-1 rounded-full bg-gothia-green/70 transition-colors duration-300 group-hover:bg-gothia-green"
          />
        </button>
      </motion.div>

      {/* Transition gradient to dark section */}
      <div className="absolute bottom-0 left-0 right-0 z-10 h-12 bg-gradient-to-b from-transparent to-black/20" />
    </section>
  );
}
